/**
 * ACL decorator script class
 *
 * @package    Decorators
 * @author     Maxim Lukianenko <mlukianenko@mindk.com>
 * @copyright  Copyright (c) 2010-2018 MindK (http://mindk.com). All rights reserved.
 * @license    http://mindk.com
 * @link       http://mindk.com
 */
import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { ROLES_HAS_JOURNAL } from 'Constants';

export default () => WrappedComponent => {
    @connect(store => ({
        user: store.auth.user,
    }))
    class AclDecorator extends PureComponent {
        /**
         * PropTypes
         */
        static propTypes = {
            user: PropTypes.object,
        };

        /**
         * @param {string} role
         * @param {int} journal
         */
        is = (role, journal) => {
            const {
                activeRole: { role : activeRole },
            } = this.props.user;

            return role === activeRole && this.checkJournal(journal);
        };

        /**
         * @param {string} role
         * @param {int} journal
         */
        isNot = (role, journal) => {
            const {
                activeRole: { role : activeRole },
            } = this.props.user;

            return role !== activeRole || !this.checkJournal(journal);
        };

        /**
         * @param {Array.<string>} roles
         * @param {int} journal
         */
        isOneOf = (roles, journal) => {
            const {
                activeRole: { role : activeRole },
            } = this.props.user;

            return !!~roles.indexOf(activeRole) && this.checkJournal(journal);
        };

        /**
         * @param {Array.<string>} roles
         * @param {int} journal
         */
        isNotOneOf = (roles, journal) => {
            const {
                activeRole: { role : activeRole },
            } = this.props.user;

            return !~roles.indexOf(activeRole) || !this.checkJournal(journal);
        };

        /**
         * @param {int} journal
         */
        checkJournal = journal => {
            const {
                activeRole: { role : activeRole, journals },
            } = this.props.user;

            return (
                !journal ||
                !~ROLES_HAS_JOURNAL.indexOf(activeRole) ||
                (Array.isArray(journals) && !!~journals.indexOf(journal))
            );
        };

        /**
         * @returns {Array.<string>}
         */
        getPermissions = () => {
            const {
                user: { roles },
            } = this.props;
            const permissions = [];
            roles.forEach(role => role.permissions.forEach(permission => permissions.push(permission.slug)));
            return permissions;
        };

        /**
         * @param {string, Array.<string>} permission
         */
        can = permission => {
            return !!~this.getPermissions().indexOf(permission);
        };

        /**
         * @param {Array.<string>} permissions
         */
        canSomeOf = permissions => {
            return permissions.some(item => !!~this.getPermissions().indexOf(item));
        };

        render() {
            return (
                <WrappedComponent
                    {...this.props}
                    is={this.is}
                    isNot={this.isNot}
                    isOneOf={this.isOneOf}
                    isNotOneOf={this.isNotOneOf}
                />
            );
        }
    }

    return AclDecorator;
};



// WEBPACK FOOTER //
// ./src/decorators/aclDecorator.jsx